import { useSupermarket } from "@/features/lifestyle/hooks/use-supermarket";
import { SettingModalHeader } from "@/features/user-profile/components/setting-modal-header";
import { SupermarketCard } from "@/features/user-profile/pages/onboarding-supermarket/supermarket-card";
import { IonSkeletonText } from "@ionic/react";
import { memo } from "react";

interface SupermarketsPageProps {
  onBack?: () => void;
}

const brandLogosMap: Record<string, string | undefined> = {
  Asda: "/assets/icons/supermarket/asda.svg",
  "M&S": "/assets/icons/supermarket/ms.svg",
  "Sainsbury's": "/assets/icons/supermarket/sainsburys.svg",
  Tesco: "/assets/icons/supermarket/tesco.svg",
  Lidl: "/assets/icons/supermarket/lidl.svg",
  Aldi: "/assets/icons/supermarket/aldi.svg",
  Morrisons: "/assets/icons/supermarket/morrisons.svg",
  Waitrose: "/assets/icons/supermarket/waitrose.svg",
} as const;

const SupermarketsPage = memo(function SupermarketsPage({ onBack }: SupermarketsPageProps) {
  const { options, selectedOptions, isLoading, handlePreferenceSelect, handleSubmit, isUpdating } =
    useSupermarket({
      onSuccess: () => {
        onBack?.();
      },
    });

  if (isLoading) {
    return (
      <div className="h-full w-full bg-white">
        <SettingModalHeader title="Where do you usually shop?" onBack={onBack} />
        <div className="px-9 pt-4 pb-20">
          <div className="grid w-full grid-cols-3 content-start gap-3">
            {Array.from({ length: 9 }).map((_, i) => (
              <IonSkeletonText key={i} animated className="h-[97px] w-[97px] rounded-[12px]" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="no-scrollbar flex h-full w-full flex-col overflow-y-auto bg-white">
      <SettingModalHeader
        title="Where do you usually shop?"
        showSaveButton
        onSave={handleSubmit}
        isSaving={isUpdating}
        onBack={onBack}
      />

      <div className="flex flex-1 flex-col items-center justify-center px-9 pt-4 pb-20">
        <div className="grid w-full grid-cols-3 content-start gap-3">
          {options?.map((option) => {
            const isSelected = selectedOptions.has(option);
            const logoSrc = brandLogosMap[option];

            return (
              <SupermarketCard
                key={option}
                option={option}
                logoSrc={logoSrc}
                isSelected={isSelected}
                isDisabled={isUpdating}
                onSelect={handlePreferenceSelect(option)}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
});

export default SupermarketsPage;
